import { QueryFunctionContext } from 'react-query'
import request from 'services/axios'

/** «نسخه‌ی اشتراکی» — matches servicePackage 1 in buySubscription/utils. */
export const SUBSCRIPTION_SERVICE_PACKAGE_ID = 1

/** Box types these endpoints understand — `utils/constants/choice.py:251`. */
export type PlanBoxType = 'mnp' | 'csb' | 'apt'

/**
 * Switch a box from the subscription plan to the commission plan.
 *
 * `box_type` used to be hardcoded to `mnp`, which is why the same screens
 * could not serve a cashbox. The backend's SwitchToCommissionView has always
 * handled `csb` too.
 */
export const setCommissionPlan = async (
    payload: number,
    boxType: PlanBoxType = 'mnp'
) => {
    const { data } = await request.post(
        `/api/v1/general/switch-to-commission/`,
        {
            box_type: boxType,
            box_id: payload,
        }
    )
    return data
}
export const initSubscription = async (ctx: QueryFunctionContext) => {
    const { data }: { data: IInitTransactionWallet } = await request.post(
        `/api/v1/payment/init/?os_type=android&app_version=1.892.9`,

        [
            {
                id: ctx.queryKey[1],
                type: 'mbr',
                amount: 0,
                cashin_type: 'hwt',
                paid_obj_id: 0,
            },
        ]
    )
    return data
}

/** Server-side price for a service package on this box. The amount scales with
 *  the fund's active-member count (one unit per 50 members), so it can't be
 *  hardcoded in the page.
 *
 *  A discount code is deliberately NOT sent here: this endpoint *consumes* the
 *  voucher just to display a price, so opening the page twice would burn two
 *  uses (hamyan-backend-2#18). Codes are checked against the read-only endpoint
 *  below instead — the same one the Android app uses. */
export const calculateSubscriptionPrice = async (ctx: QueryFunctionContext) => {
    // queryKey: ['subscription-price', boxId, monthCount, boxType?]
    const [, boxId, monthCount, boxType] = ctx.queryKey
    const { data }: { data: ICalculateSubscriptionPrice } = await request.post(
        `/api/v1/settings/calculate-price-service-package/`,
        {
            box_type: (boxType as PlanBoxType) ?? 'mnp',
            box_id: Number(boxId),
            month_count: Number(monthCount),
            service_package_id: SUBSCRIPTION_SERVICE_PACKAGE_ID,
        }
    )
    return data
}

/** Validates a discount code without spending it. Read-only: it never writes a
 *  `consumeDiscount` row, so the code can be checked as the user types.
 *  The server 404s an unknown code and 403s one that is already used up or not
 *  targeted at this member — but it does NOT check expiry, so callers must
 *  compare `valid_until` themselves (Android does the same). */
export const validateDiscountCode = async (code: string) => {
    const { data }: { data: IDiscount } = await request.get(
        `/api/v1/payment/discounts/${encodeURIComponent(code)}/`
    )
    return data
}

/** Buys the subscription through the v2 gateway flow. The old v1
 *  `/payment/orders/` path is unusable here: it ends in `Gateway.request_pay()`,
 *  which PSG_IPG (the only gateway the PWA shows) does not implement. */
export const setSubscriptionPayment = async (
    payload: ISetSubscriptionPaymentPayload
) => {
    const { data }: { data: IinitGatewayPayment } = await request.post(
        `/api/v2/payment/gateways/gateway-payment/`,
        {
            clientType: 'pwa',
            gateway: { uniqueCode: payload.gatewayCode },
            paymentItems: [
                {
                    // itemType decides which product the Order is attached to;
                    // `csb` is what makes the purchase clear a cashbox's
                    // «در انتظار پرداخت اشتراک» state.
                    itemType: payload.boxType ?? 'mnp',
                    itemId: payload.ctxId,
                    box_id: payload.ctxId,
                    itemAmount: payload.amount,
                    description: 'خرید اشتراک',
                    order: {
                        servicePackage: SUBSCRIPTION_SERVICE_PACKAGE_ID,
                        monthCount: payload.monthCount,
                        discountCode: payload.discountCode ?? null,
                    },
                },
            ],
        }
    )
    return data
}

/** A 100%-off code leaves nothing to pay, and the v2 flow rejects a zero total
 *  ("Amount must be greater than 0"). The v1 order path handles exactly this
 *  case: `OrderView.create` skips the gateway and calls `activate_box_order`
 *  when the discount is 100%. That branch never reaches the unimplemented
 *  `request_pay`, so it works from the PWA — and it is the same path Android
 *  takes when it hides the gateway list for a full discount. */
export const createFullDiscountOrder = async (
    payload: IFullDiscountOrderPayload
) => {
    const { data } = await request.post(`/api/v1/payment/orders/`, {
        transaction: { source: 'hwt' },
        service_package: SUBSCRIPTION_SERVICE_PACKAGE_ID,
        ctx_id: payload.ctxId,
        ctx_type: 'mnp',
        month_count: payload.monthCount,
        discount_code: payload.discountCode,
    })
    return data
}
